import { nextThreadAction, projectActionFactsFromEntries } from "@afar-research/afar";
import type { EnvironmentId, ProjectListEntriesResult } from "@afar-research/contracts";
import { useAtomValue } from "@effect/atom-react";
import * as Option from "effect/Option";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useMemo } from "react";

import { getProjectEntriesQueryAtom } from "../components/files/projectFilesQueryState";

const NO_PROJECT_ENTRIES: Atom.Atom<AsyncResult.AsyncResult<ProjectListEntriesResult, unknown>> =
  Atom.make(AsyncResult.initial<ProjectListEntriesResult, unknown>());

/**
 * The Next Action for a thread (CONTEXT.md: Next Action), derived from the
 * project's files. Reads the project listing the file tree and the Brain
 * Delete picker already share, so it costs no request of its own, and derives
 * the facts once per listing rather than per keystroke.
 */
export function useNextThreadAction(input: {
  readonly environmentId: EnvironmentId | null;
  readonly projectRoot: string | null;
}): string | null {
  const atom =
    input.environmentId !== null && input.projectRoot !== null
      ? getProjectEntriesQueryAtom(input.environmentId, input.projectRoot)
      : NO_PROJECT_ENTRIES;
  const result = useAtomValue(
    atom as Atom.Atom<AsyncResult.AsyncResult<ProjectListEntriesResult, unknown>>,
  );
  const entries = Option.getOrNull(AsyncResult.value(result))?.entries ?? null;
  const project = useMemo(
    () => (entries === null ? null : projectActionFactsFromEntries(entries)),
    [entries],
  );
  // Until the listing arrives nothing is suggested rather than a guess.
  return project === null ? null : nextThreadAction({ project });
}
